import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Button, FormControl, Text, useDisclosure, useToast } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import type { InsuranceParamsDto, VinOnlineVehicleDto } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { transformVIN } from 'BrokerTsLib/src/Utils/vin'
import { useRef, useState } from 'react'

import { FormGroup } from '@/components/FormGroup'
import { FormLabel } from '@/components/FormLabel'
import { InputGroup } from '@/components/InputGroup'
import { Panel } from '@/components/Panel'
import { VehicleInformation } from '@/components/VehicleInformation'
import { useFindVehicle } from '@/hooks/useVehicle'
import { Cloud } from '@/icons'

export const VehicleIdentificationForm = ({ onEnterVinData }: {
  onEnterVinData: (data: VinOnlineVehicleDto) => void
}) => {
  const { register, watch, setValue, formState: { errors, defaultValues } } = useFormContext<InsuranceParamsDto>()
  const { mutateAsync: findVehicle, isPending: isLoadingFindVehicle } = useFindVehicle()
  const toast = useToast()
  const [vinOnlineData, setVinOnlineData] = useState<VinOnlineVehicleDto | null>(null)

  const vehicleInfoCancelRef = useRef<HTMLButtonElement>(null)
  const {
    isOpen: isVehicleInfoAlertOpen,
    onOpen: vehicleInfoAlertOnOpen,
    onClose: vehicleInfoAlertOnClose,
  } = useDisclosure()

  const isContracted = !!defaultValues?.generalParams?.contractNumber

  const vehicle = watch('vehicle')

  const vehicleIdentificationParams = {
    paramsId: defaultValues?.generalParams?.paramsId?.toString() ?? undefined,
    spz: vehicle.licensePlate ?? undefined,
    vin: vehicle.vin ?? undefined,
    techCertNumber: vehicle.cisloTechnickehoPrukazu ?? undefined,
  }

  const handleEnterVinData = () => {
    if (!vinOnlineData) return

    onEnterVinData(vinOnlineData)
    vehicleInfoAlertOnOpen()
  }

  const inputIsDifferentToVinData = vehicle.licensePlate !== vinOnlineData?.registrationMark
        || vehicle.vin !== vinOnlineData?.vin
        || vehicle.cisloTechnickehoPrukazu !== vinOnlineData?.TechnicalCertificateNumber

  return <>
    <FormGroup variant="carRegistration">
      <FormGroup>
        {/* vehicle.licensePlate */}
        <FormControl isInvalid={!!errors.vehicle?.licensePlate}>
          <FormLabel htmlFor="vehicle.licensePlate"
            tooltip="Zadejte SPZ vozidla. Pokud SPZ neznáte, nechte pole prázdné.">Reg. značka (SPZ)</FormLabel>
          <InputGroup
            isDisabled={isContracted}
            id="vehicle.licensePlate"
            placeholder="SPZ"
            htmlSize={8}
            autoComplete="on"
            maxLength={10}
            errors={errors.vehicle?.licensePlate}
            {...register('vehicle.licensePlate', {
              pattern: /[a-zA-Z]{6,8}/gi,
              minLength: 7,
              onChange: (e) => { setValue('vehicle.licensePlate', e.target.value.toUpperCase()) }
            })}
          />
        </FormControl>

        {/* vehicle.cisloTechnickehoPrukazu */}
        <FormControl isInvalid={!!errors.vehicle?.cisloTechnickehoPrukazu}>
          <FormLabel htmlFor="vehicle.cisloTechnickehoPrukazu"
            tooltip="Zadejte číslo velkého technické průkazu. Pokud číslo neznáte, nechte pole prázdné.">Číslo velkého TP</FormLabel>
          <InputGroup
            isDisabled={isContracted}
            id="vehicle.cisloTechnickehoPrukazu"
            placeholder="Číslo TP"
            htmlSize={8}
            autoComplete="off"
            errors={errors.vehicle?.cisloTechnickehoPrukazu}
            {...register('vehicle.cisloTechnickehoPrukazu', {
              minLength: 8,
              maxLength: 8,
              onChange: (e) => { setValue('vehicle.cisloTechnickehoPrukazu', e.target.value.toUpperCase()) }
            })}
          />
        </FormControl>

        <Text variant="textMuted" sx={{ alignSelf: 'end', height: '30px' }}>
          nebo
        </Text>

        {/* VIN */}
        <FormControl isInvalid={!!errors.vehicle?.vin}>
          <FormLabel htmlFor="vehicle.vin" tooltip="Zadejte identifikační číslo vozidla (VIN), které naleznete v technickém průkazu.">
            VIN
          </FormLabel>
          <InputGroup
            isDisabled={isContracted}
            id="vehicle.vin"
            placeholder="Např: WMWXP7C54G2A43037"
            htmlSize={18}
            maxLength={17}
            autoComplete="on"
            errors={errors.vehicle?.vin}
            {...register('vehicle.vin', {
              onChange: (e) => { setValue('vehicle.vin', transformVIN(e.target.value)) }
            })}
          />
        </FormControl>
      </FormGroup>

      {!isContracted &&
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setVinOnlineData(null)
                    findVehicle(vehicleIdentificationParams)
                      .then(response => {
                        if (!response.data) {
                          toast({
                            status: 'warning',
                            title: 'Údaje o vozidle nebyly nalezeny.',
                            duration: 5000,
                          })
                          setVinOnlineData(null)
                        }
                        else
                          setVinOnlineData(response.data)

                        return response
                      })
                      .catch((err: AxiosError) => {
                        toast({
                          status: 'error',
                          title: 'Chyba při načítání údajů o vozidle.',
                        })
                      })
                  }}
                  isLoading={isLoadingFindVehicle}
                  isDisabled={(!vehicle.licensePlate && !vehicle.vin) || !inputIsDifferentToVinData}
                >
                  <Cloud color="secondary.500" />
                  Načíst údaje o vozidle
                </Button>
      }
    </FormGroup>
    {vinOnlineData && (
      <Panel>
        {!vinOnlineData && (
          <Text variant="warning">
            Nepodařilo se získat informace o vozidle. Doplňte údaje o
            vozidle ručně podle technického průkazu.
          </Text>
        )}

        {!!vinOnlineData && (
          <>
            <VehicleInformation
              vehicle={vinOnlineData}
              error={vinOnlineData?.vysledekKontrolyVinTextove}
            />

            <Button
              type="button"
              variant="outline"
              onClick={() => handleEnterVinData()}
            >
              Vložit informace do nabídky
            </Button>
          </>
        )}

        <AlertDialog
          leastDestructiveRef={vehicleInfoCancelRef}
          isOpen={isVehicleInfoAlertOpen}
          onClose={vehicleInfoAlertOnClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Vyhledání vozidla
              </AlertDialogHeader>

              <AlertDialogBody>
                {vinOnlineData.error && (
                  <>
                    <strong>
                      Nebyly nalezeny všechny parametry vozidla
                    </strong>
                    , doplňte prosím údaje ručně a zkontrolujte
                    automaticky vyplněné údaje.
                  </>
                )}
                {!vinOnlineData.error && (
                  <>
                    Data o vozidle byla vložena do formuláře.{' '}
                    <strong>
                      Prosím zkontrolujte správnost údajů
                    </strong>
                    .
                  </>
                )}
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  ref={vehicleInfoCancelRef}
                  colorScheme="primary"
                  onClick={vehicleInfoAlertOnClose}
                >
                  Rozumím
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Panel >
    )}
  </>
}